/* eslint-disable consistent-return */
/* eslint-disable @typescript-eslint/no-shadow */
import { useState, useEffect, useRef } from 'react';
import {
  doc,
  collection,
  query,
  where,
  onSnapshot,
  documentId,
} from 'firebase/firestore';
import {
  capturePosthogException,
  capturePosthogMessage,
} from '../../shared/utils/posthog-error-reporting';
import { db } from '../../firebase/config';
import { validateTrackingId } from './firebase-auth';

interface FirebaseData {
  accessibleUserIds?: string[];
  [key: string]: any;
}

export const useFirebaseData = (
  uid?: string,
  seedTestId?: string,
  collectionName?: string,
  shouldStart?: boolean,
  changeFireBaseConnected?: (isActive: boolean) => void,
) => {
  const [data, setData] = useState<FirebaseData | null>(null);

  const [isConnectionPending, setIsConnectionPending] = useState(false);

  useEffect(() => {
    // Don't fetch if no testId is provided
    if (!shouldStart || !seedTestId) {
      setData(null);
      setIsConnectionPending(false);
      return;
    }

    let unsubscribe: (() => void) | undefined;

    const validateAndFetch = async () => {
      try {
        setIsConnectionPending(true);

        // Validate tracking ID first
        const hasAccess = await validateTrackingId(collectionName, seedTestId);
        if (!hasAccess) {
          setData(null);
          setIsConnectionPending(false);

          if (process.env.NODE_ENV === 'production') {
            capturePosthogMessage(
              'Firebase access denied: Invalid ID or document does not exist',
              {
                tags: {
                  firebase: 'access-denied',
                },
                extra: {
                  seedTestId,
                },
              },
            );
          }
          return;
        }

        // Now set up the Firebase listener
        unsubscribe = onSnapshot(
          doc(db, collectionName, seedTestId),
          (docSnapshot) => {
            if (docSnapshot.exists()) {
              changeFireBaseConnected?.(true);
              const newData = {
                ...docSnapshot.data(),
              } as FirebaseData;

              setData(newData);
              setIsConnectionPending(false);
            } else {
              setData(null);
              changeFireBaseConnected?.(false);
              setIsConnectionPending(false);

              // Log to PostHog with firebase key
              if (process.env.NODE_ENV === 'production') {
                capturePosthogMessage('Firebase document does not exist', {
                  tags: {
                    firebase: 'document-not-found',
                  },
                  extra: {
                    seedTestId,
                    collection: collectionName,
                  },
                });
              }
            }
          },
          (error) => {
            setIsConnectionPending(false);
            // Log Firebase error to PostHog with firebase key
            if (process.env.NODE_ENV === 'production') {
              capturePosthogException(error, {
                tags: {
                  firebase: 'firestore-error',
                },
                extra: {
                  seedTestId,
                  collection: collectionName,
                  errorCode: error.code,
                },
              });
            }
          },
        );
      } catch (error: any) {
        setIsConnectionPending(false);
        // Log general Firebase error to PostHog with firebase key
        if (process.env.NODE_ENV === 'production') {
          capturePosthogException(error, {
            tags: {
              firebase: 'general-error',
            },
            extra: {
              seedTestId,
              operation: 'validateAndFetch',
            },
          });
        }
      }
    };

    validateAndFetch();

    // Cleanup function
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
      changeFireBaseConnected?.(false);
      setIsConnectionPending(false);
    };
  }, [seedTestId, shouldStart, collectionName]);

  return {
    data,
    isConnectionPending,
  };
};

export const useFirebaseDataForMultipleDocIds = (
  jobIds?: string[],
  collectionName?: string,
  shouldStart?: boolean,
  changeFireBaseConnected?: (isActive: boolean) => void,
) => {
  const [data, setData] = useState<FirebaseData[]>([]);
  const [isConnectionEstablished, setIsConnectionEstablished] = useState(false);
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    // Clear data only when explicitly disabled or invalid params (but not for empty array)
    if (!shouldStart || !jobIds || !collectionName) {
      setData([]);
      setIsConnectionEstablished(false);
      return;
    }

    // If jobIds is empty array, don't set up listeners but keep existing data
    if (jobIds.length === 0) {
      setIsConnectionEstablished(false);
      return;
    }

    setIsConnectionEstablished(true);
    const unsubscribers: Array<() => void> = [];
    const shutdownConnection = () => {
      unsubscribers.forEach((unsub) => unsub());
      changeFireBaseConnected?.(false);
      setIsConnectionEstablished(false);
    };

    try {
      // OPTION 1: Use single query with 'in' operator for ≤10 documents
      if (jobIds.length <= 10) {
        const q = query(
          collection(db, collectionName),
          where(documentId(), 'in', jobIds),
        );

        const unsubscribe = onSnapshot(
          q,
          (querySnapshot) => {
            changeFireBaseConnected?.(true);
            const newDocuments: FirebaseData[] = [];
            const fetchedDocIds = new Set<string>();
            querySnapshot.docs.forEach((doc) => {
              const docData = doc.data();
              newDocuments.push({
                id: doc.id,
                ...docData,
              } as FirebaseData);
              fetchedDocIds.add(doc.id);
            });
            // Merge new data with existing data to persist previous jobIds
            setData((prevData) => {
              // Create a map of existing data by id for efficient lookup
              const existingMap = new Map(
                prevData.map((item) => [item.id, item]),
              );
              // Update or add new documents
              newDocuments.forEach((doc) => {
                existingMap.set(doc.id, doc);
              });
              // Convert back to array
              return Array.from(existingMap.values());
            });
            const missingIds = jobIds.filter(
              (jobId) => !fetchedDocIds.has(jobId),
            );
            if (missingIds.length > 0) {
              shutdownConnection();
              return;
            }
            setIsConnectionEstablished(true);
          },
          (error) => {
            setIsConnectionEstablished(false);
            changeFireBaseConnected?.(false);

            if (process.env.NODE_ENV === 'production') {
              capturePosthogException(error, {
                tags: { firebase: 'query-listener-error' },
                extra: {
                  jobIds,
                  collection: collectionName,
                  errorCode: error.code,
                  strategy: 'single-query',
                },
              });
            }
          },
        );

        unsubscribers.push(unsubscribe);
      }
      // OPTION 2: Use multiple individual listeners for >10 documents
      else {
        jobIds.forEach((jobId) => {
          const unsubscribe = onSnapshot(
            doc(db, collectionName, jobId),
            (docSnapshot) => {
              if (docSnapshot.exists()) {
                const docData = docSnapshot.data();
                const jobData = {
                  id: docSnapshot.id,
                  ...docData,
                } as FirebaseData;

                // Merge new job data with existing data to persist previous jobIds
                setData((prevData) => {
                  const existingIndex = prevData.findIndex(
                    (item) => item.id === jobId,
                  );
                  if (existingIndex >= 0) {
                    // Update existing item
                    const newData = [...prevData];
                    newData[existingIndex] = jobData;
                    return newData;
                  }
                  // Add new item
                  return [...prevData, jobData];
                });
                changeFireBaseConnected?.(true);
              } else {
                // Remove job data if document doesn't exist
                setData((prevData) =>
                  prevData.filter((item) => item.id !== jobId),
                );
                shutdownConnection();
                return;
              }
              setIsConnectionEstablished(true);
            },
            (error) => {
              setIsConnectionEstablished(false);

              if (process.env.NODE_ENV === 'production') {
                capturePosthogException(error, {
                  tags: { firebase: 'individual-listener-error' },
                  extra: {
                    jobId,
                    collection: collectionName,
                    errorCode: error.code,
                    strategy: 'multiple-listeners',
                  },
                });
              }
            },
          );

          unsubscribers.push(unsubscribe);
        });
      }
    } catch (error: any) {
      setIsConnectionEstablished(false);
      if (process.env.NODE_ENV === 'production') {
        capturePosthogException(error, {
          tags: { firebase: 'setup-error' },
          extra: {
            jobIds,
            collection: collectionName,
            jobIdsCount: jobIds.length,
          },
        });
      }
    }

    // Cleanup all listeners
    const cleanup = () => {
      unsubscribers.forEach((unsub) => unsub());
      changeFireBaseConnected?.(false);
      setIsConnectionEstablished(false);
    };

    cleanupRef.current = cleanup;
    return cleanup;
  }, [jobIds?.join(','), collectionName, shouldStart]);

  return {
    data,
    isConnectionEstablished,
    cleanup: () => cleanupRef.current?.(),
  };
};
